import React, { useMemo, useRef, useState } from 'react';
import { AppBar, Avatar, Box, Divider, Drawer, IconButton, InputBase, List, ListItem, ListItemAvatar, ListItemText, Paper, Toolbar, Typography, Dialog, DialogTitle, DialogContent, Fab, Grid, Button, TextField, CircularProgress } from '@mui/material';
import { Menu as MenuIcon, Add as AddIcon, DocumentScannerOutlined, AssignmentOutlined, MailOutline, DescriptionOutlined, WarningAmberOutlined, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { jsPDF } from 'jspdf';
import MobileInquiryForm from '../projects/MobileInquiryForm';
import GeneratePocrDialog from '../projects/GeneratePocrDialog';
import GeneratePokrDialog from '../projects/GeneratePokrDialog';
import subcontractorService, { Subcontractor } from '../../services/subcontractorService';
import projectService, { Project } from '../../services/projectService';
import api from '../../services/api';
import { useNotification } from './Layout';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { onIncomingCall } from '../../services/callService';
import { buildCallKey, broadcastCallResolved, onCallResolvedFromOtherTab } from '../../services/callCoordination';
import IncomingCallToast from '../common/IncomingCallToast';
import { useInternalChatNotifySound } from '../../hooks/useInternalChatNotifySound';

// Minimalny layout mobilny inspirowany Gmail: hamburger, wyszukiwarka na środku, avatar po prawej.

const drawerWidth = 300;

const MobileLayout: React.FC = () => {
  const { user, isAuthenticated, logout, isLoading } = useAuth();
  useInternalChatNotifySound(isAuthenticated, isLoading);
  const { showNotification } = useNotification();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const [profileOpen, setProfileOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [pocrPokrOpen, setPocrPokrOpen] = useState(false);
  const scanInputRef = useRef<HTMLInputElement | null>(null); // Files -> Skanuj dokumenty (PDF)
  const fileInputRef = useRef<HTMLInputElement | null>(null); // Aparat (zdjęcia)
  const libraryInputRef = useRef<HTMLInputElement | null>(null); // Biblioteka zdjęć
  const [projectPickerOpen, setProjectPickerOpen] = useState<null | 'scan' | 'inquiry' | 'pocr' | 'pokr'>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectSearch, setProjectSearch] = useState('');
  const [pickedProject, setPickedProject] = useState<Project | null>(null);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [subPickerOpen, setSubPickerOpen] = useState(false);
  const [subsLoading, setSubsLoading] = useState(false);
  const [subs, setSubs] = useState<Subcontractor[]>([]);
  const [pickedSub, setPickedSub] = useState<Subcontractor | null>(null);
  const [pocrOpen, setPocrOpen] = useState(false);
  const [pokrOpen, setPokrOpen] = useState(false);
  const [okOpen, setOkOpen] = useState(false);
  const [okTitle, setOkTitle] = useState('');
  const [okSubtitle, setOkSubtitle] = useState('');
  const [scanChoiceOpen, setScanChoiceOpen] = useState(false);
  const [incomingOpen, setIncomingOpen] = useState(false);
  const [incomingFrom, setIncomingFrom] = useState<string | null>(null);
  const [incomingAvatar, setIncomingAvatar] = useState<string | null>(null);
  const [incomingMeet, setIncomingMeet] = useState<string | null>(null);
  const [incomingCallKey, setIncomingCallKey] = useState<string | null>(null);
  const [incomingLeftSec, setIncomingLeftSec] = useState(30);

  const menu = useMemo(() => [
    { key: 'home', text: 'Strona główna', path: '/' },
    { key: 'projects', text: 'Projekty', path: '/projects' },
    { key: 'offers', text: 'Oferty', path: '/offers' },
    { key: 'database', text: 'Bazy danych', path: '/database' },
    { key: 'apps', text: 'Aplikacje', path: '/app' },
    { key: 'invoices', text: 'Faktury', path: '/accounting/purchases' },
    { key: 'bug', text: 'Zgłoś błąd', path: '/bug-report' },
    { key: 'settings', text: 'Ustawienia', path: '/settings' },
  ], []);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = q.trim();
    if (trimmed.length === 0) return;
    navigate(`/search?q=${encodeURIComponent(trimmed)}`);
  };

  const handleScan = () => {
    setProjectPickerOpen('scan');
  };
  const createPdfFromImages = async (files: File[]): Promise<Blob> => {
    const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
    let firstPage = true;
    for (const f of files) {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result as string);
        r.onerror = reject;
        r.readAsDataURL(f);
      });
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const im = new Image();
        im.onload = () => resolve(im);
        im.onerror = reject;
        im.src = dataUrl;
      });
      const w = pdf.internal.pageSize.getWidth();
      const h = pdf.internal.pageSize.getHeight();
      const ratio = Math.min(w / img.width, h / img.height);
      const iw = img.width * ratio; const ih = img.height * ratio;
      if (!firstPage) pdf.addPage();
      firstPage = false;
      pdf.addImage(img, 'JPEG', (w - iw) / 2, (h - ih) / 2, iw, ih);
    }
    return pdf.output('blob');
  };

  const onFilesSelected: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const files = Array.from(e.currentTarget.files || []);
    e.currentTarget.value = '';
    if (!pickedProject) {
      showNotification('Nie wybrano projektu', 'error');
      return;
    }
    if (files.length === 0) {
      showNotification('Nie wybrano plików', 'warning');
      return;
    }
    (async () => {
      try {
        showNotification('Wgrywanie skanów…', 'info');
        const parentFolderId = await ensureRoboczeFolderId(pickedProject);
        for (const file of files) {
          const form = new FormData();
          form.append('parentFolderId', parentFolderId);
          form.append('file', file, file.name);
          await api.post('/drive/files', form, { headers: { 'Content-Type': 'multipart/form-data' } });
        }
        // Okejka w stylu mobilnym
        setOkTitle('Zapisano skan');
        setOkSubtitle(`Folder: 10_ROBOCZE • Projekt ${pickedProject.project_id}`);
        setOkOpen(true);
        setTimeout(()=> setOkOpen(false), 1200);
        showNotification('Skan zapisany w 10_ROBOCZE', 'success');
      } catch (err) {
        console.error('Upload scan error', err);
        showNotification('Nie udało się zapisać skanu', 'error');
      } finally {
        setActionsOpen(false);
        setProjectPickerOpen(null);
        setPickedProject(null);
        setScanChoiceOpen(false);
      }
    })();
  };

  const handleInquiry = () => {
    setProjectPickerOpen('inquiry');
  };

  const handleReport = () => {
    setActionsOpen(false);
    // Tymczasowo kierujemy do strony głównej; logika raportu będzie dodana w przyszłości
    navigate('/');
  };

  const openPocrPokr = () => {
    setPocrPokrOpen(true);
  };

  const handlePocr = () => {
    setPocrPokrOpen(false);
    setProjectPickerOpen('pocr');
  };

  const handlePokr = () => {
    setPocrPokrOpen(false);
    setProjectPickerOpen('pokr');
  };

  const handleWezwanie = () => {
    setActionsOpen(false);
    navigate('/projects');
  };

  // Helpers
  const extractDriveId = (viewLink?: string): string | undefined => {
    if (!viewLink) return undefined;
    const byFolder = viewLink.match(/\/folders\/([^\\/?]+)/);
    const byFileD = viewLink.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
    const byDocD = viewLink.match(/\/(?:document|spreadsheets|presentation)\/d\/([a-zA-Z0-9_-]+)/);
    const byPlainD = viewLink.match(/\/(?:u\/\d\/)?d\/([a-zA-Z0-9_-]+)/);
    const byQuery = viewLink.match(/[?&]id=([a-zA-Z0-9_-]+)/);
    return byFolder?.[1] || byFileD?.[1] || byDocD?.[1] || byPlainD?.[1] || byQuery?.[1];
  };

  const ensureRoboczeFolderId = async (project: Project): Promise<string> => {
    const parentId = extractDriveId(project.google_drive_link || '');
    if (!parentId) throw new Error('Brak linku do folderu projektu');
    const res = await api.get('/drive/files', { params: { folderId: parentId, isSharedDrive: true, pageSize: 200 } });
    const files: Array<any> = res.data?.data || [];
    let folder = files.find((f: any) => f.mimeType === 'application/vnd.google-apps.folder' && f.name === '10_ROBOCZE');
    if (folder?.id) return folder.id;
    const created = await api.post('/drive/folders', { folderName: '10_ROBOCZE', parentFolderId: parentId });
    return created.data?.data?.id || created.data?.id;
  };

  // Load projects for picker
  React.useEffect(() => {
    if (!projectPickerOpen) return;
    setProjectsLoading(true);
    projectService.getAllProjects().then(setProjects).catch(()=>setProjects([])).finally(()=>setProjectsLoading(false));
  }, [projectPickerOpen]);

  const visibleProjects = React.useMemo(() => {
    const ql = projectSearch.toLowerCase();
    return projects
      .filter(p => p.project_phase === 'W REALIZACJI')
      .filter(p => (`${p.project_id} ${p.project_name}`.toLowerCase().includes(ql)));
  }, [projects, projectSearch]);

  React.useEffect(() => {
    if (!isAuthenticated || isLoading) return;
    const off = onIncomingCall(({ fromUserId, fromDisplayName, fromAvatarUrl, meetLink, toUserId }) => {
      const dnd = (localStorage.getItem('chat:dnd') || '0') === '1';
      if (dnd) return;
      if (!user?.user_id || (toUserId && toUserId !== user.user_id)) return;
      setIncomingFrom(fromDisplayName || fromUserId || 'Użytkownik');
      setIncomingAvatar(fromAvatarUrl || null);
      setIncomingMeet(meetLink);
      setIncomingCallKey(buildCallKey({ meetLink, fromUserId, toUserId: toUserId || user?.user_id }));
      setIncomingOpen(true);
      setIncomingLeftSec(30);
    });
    const offResolved = onCallResolvedFromOtherTab(({ callKey }) => {
      setIncomingCallKey((current) => {
        if (current && current === callKey) {
          setIncomingOpen(false);
          setIncomingFrom(null);
          setIncomingAvatar(null);
          setIncomingMeet(null);
          return null;
        }
        return current;
      });
    });
    return () => {
      try { (off as () => void)?.(); } catch { /* ok */ }
      try { offResolved(); } catch { /* ok */ }
    };
  }, [isAuthenticated, isLoading, user?.user_id]);

  React.useEffect(() => {
    if (!incomingOpen) return;
    const endAt = Date.now() + 30000;
    let rafId = 0;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      const left = Math.max(0, Math.ceil((endAt - Date.now()) / 1000));
      setIncomingLeftSec(left);
      if (left <= 0) {
        setIncomingOpen(false);
        setIncomingFrom(null);
        setIncomingAvatar(null);
        setIncomingMeet(null);
        setIncomingCallKey(null);
        return;
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      try { cancelAnimationFrame(rafId); } catch { /* ok */ }
    };
  }, [incomingOpen]);

  // When project picked, branch by action
  React.useEffect(() => {
    if (!pickedProject || !projectPickerOpen) return;
    if (projectPickerOpen === 'inquiry') {
      setInquiryOpen(true);
      setActionsOpen(false);
    } else if (projectPickerOpen === 'scan') {
      // pokaż wybór metody skanowania (Files/iOS, Aparat, Biblioteka)
      setScanChoiceOpen(true);
    } else if (projectPickerOpen === 'pocr' || projectPickerOpen === 'pokr') {
      setSubPickerOpen(true);
      setSubsLoading(true);
      subcontractorService.getSubcontractorsByProjectId(pickedProject.project_id).then((list)=>{
        setSubs(Array.isArray(list) ? list : []);
      }).catch(()=>setSubs([])).finally(()=>setSubsLoading(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedProject, projectPickerOpen]);

  const isEmbed = React.useMemo(() => {
    try { return new URLSearchParams(location.search).get('embed') === '1'; } catch { return false; }
  }, [location.search]);
  const contentTopMargin = isEmbed
    ? 'env(safe-area-inset-top)'
    : 'calc(64px + env(safe-area-inset-top) + var(--legacy-host-banner-height, 0px))';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {!isEmbed && (
        <AppBar
          position="fixed"
          elevation={0}
          color="transparent"
          sx={{
            top: 'var(--legacy-host-banner-height, 0px)',
            backgroundColor: 'transparent',
            boxShadow: 'none',
            pt: 'max(env(safe-area-inset-top), 8px)',
          }}
        >
          <Toolbar sx={{ gap: 1, px: 2 }}>
            {/* Pasek wyszukiwania z hamburgerem i avatarem wewnątrz */}
            <Paper
              component="form"
              onSubmit={onSubmit}
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                px: 1.5,
                py: 0.75,
                borderRadius: 2,
                backgroundColor: '#fff',
                border: 'none',
                boxShadow: '0 1px 2px rgba(60,64,67,0.3), 0 1px 3px 1px rgba(60,64,67,0.15)',
                width: '92%',
                mx: 'auto',
                transform: 'translateY(20%)',
                position: 'relative'
              }}
            >
              <IconButton onClick={() => setOpen(true)} aria-label="menu" sx={{ mr: 0.5, color: 'text.secondary' }}>
                <MenuIcon />
              </IconButton>
              <InputBase placeholder="" value={q} onChange={(e)=>setQ(e.target.value)} sx={{ flex: 1 }} />
              {q.length === 0 && (
                <Box sx={{ position:'absolute', left: 56, right: 56, pointerEvents:'none' }}>
                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    Szukaj w <Box component="span" sx={{ color: '#c53030', opacity: 0.5, fontFamily: 'Montserrat, Roboto, sans-serif', fontWeight: 700, textTransform: 'uppercase' }}>UNITRESCO</Box>
                  </Typography>
                </Box>
              )}
              <IconButton aria-label="profil" sx={{ ml: 0.5 }} onClick={()=>setProfileOpen(true)}>
                <Avatar src={user?.avatar_url} sx={{ width: 28, height: 28 }} />
              </IconButton>
            </Paper>
          </Toolbar>
        </AppBar>
      )}

      {/* Drawer z modułami */}
      <Drawer anchor="left" open={open} onClose={() => setOpen(false)} PaperProps={{ sx: { width: drawerWidth } }}>
        <Box sx={{ px: 2, py: 2, display: 'flex', alignItems: 'center' }}>
          <Typography
            variant="h1"
            component="h1"
            sx={{
              fontFamily: "'Roboto', sans-serif",
              fontSize: '1.5rem',
              fontWeight: 700,
              color: '#c53030',
              letterSpacing: '2px',
              textTransform: 'uppercase',
              m: 0
            }}
          >
            Unitresco
          </Typography>
        </Box>
        <Divider />
        <List>
          {menu.map((m) => (
            <ListItem key={m.key} button component={Link} to={m.path} onClick={()=>setOpen(false)} selected={m.key === 'invoices' ? location.pathname.startsWith('/accounting') : location.pathname===m.path}>
              <ListItemText primary={m.text} />
            </ListItem>
          ))}
        </List>
        <Divider />
        <List>
          <ListItem button component={Link} to="/help" onClick={()=>setOpen(false)}>
            <ListItemText primary="Pomoc" />
          </ListItem>
        </List>
      </Drawer>

      {/* Zawartość */}
      <Box component="main" sx={{ flex: '1 0 auto', px: 2, py: 2, mt: contentTopMargin }}>
        {isAuthenticated ? <Outlet /> : (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="body2" color="text.secondary">Zaloguj się, aby korzystać z modułów.</Typography>
          </Box>
        )}
      </Box>

      {/* Modal profilu w stylu mobilnym */}
      <Dialog 
        open={profileOpen} 
        onClose={()=>setProfileOpen(false)} 
        fullWidth 
        maxWidth="xs"
        sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
        PaperProps={{ sx:{ borderRadius: 3, mt: 'calc(88px + env(safe-area-inset-top))' } }}
      >
        <DialogTitle sx={{ display:'grid', gridTemplateColumns:'auto 1fr auto', alignItems:'center', columnGap: 1, pr:1 }}>
          <IconButton onClick={()=>setProfileOpen(false)} aria-label="zamknij" sx={{ justifySelf:'start', width: 40, height: 40 }}>
            <span style={{ fontSize: 20 }}>✕</span>
          </IconButton>
          <Typography 
            variant="h6" 
            sx={{ 
              textAlign:'center', fontWeight:700, letterSpacing:'2px', color:'#c53030', textTransform:'uppercase'
            }}
          >
            Unitresco
          </Typography>
          <Box sx={{ width: 40, height: 40 }} />
        </DialogTitle>
        <DialogContent dividers>
          <List>
            <ListItem button onClick={()=>{ setProfileOpen(false); navigate('/profile'); }}>
              <ListItemAvatar><Avatar src={user?.avatar_url} /></ListItemAvatar>
              <ListItemText primary="Mój profil" secondary={user?.email} />
            </ListItem>
          </List>
          <Divider />
          <List>
            <ListItem button onClick={()=>{ setProfileOpen(false); navigate('/settings'); }}>
              <ListItemText primary="Wygląd" />
            </ListItem>
            <ListItem button onClick={()=>{ setProfileOpen(false); try { (logout as any)?.(); } catch {} }}> 
              <ListItemText primary="Wyloguj" />
            </ListItem>
          </List>
        </DialogContent>
      </Dialog>

      {/* Picker projektu do: skanowania / zapytania / POCR/POKR */}
      <Dialog open={!!projectPickerOpen} onClose={()=>{ setProjectPickerOpen(null); setPickedProject(null); }} fullWidth maxWidth="xs">
        <DialogTitle sx={{ display:'grid', gridTemplateColumns:'auto 1fr auto', alignItems:'center', columnGap: 1, pr:1 }}>
          <Box sx={{ width: 40, height: 40 }} />
          <Typography variant="h6" sx={{ textAlign:'center', fontWeight:700 }}>
            {projectPickerOpen === 'scan' ? 'Skanuj dokumenty' : 'Wybierz projekt'}
          </Typography>
          <IconButton onClick={()=>{ setProjectPickerOpen(null); setPickedProject(null); }} aria-label="zamknij" sx={{ justifySelf:'end', width: 40, height: 40 }}>
            <span style={{ fontSize: 20 }}>✕</span>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {projectPickerOpen === 'scan' && (
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Wybierz projekt</Typography>
          )}
          <TextField fullWidth size="small" placeholder="Szukaj projektu" value={projectSearch} onChange={(e)=>setProjectSearch(e.target.value)} sx={{ mb: 1 }} />
          {projectsLoading ? (
            <Box sx={{ display:'flex', justifyContent:'center', py: 2 }}><CircularProgress size={20} /></Box>
          ) : (
            <List>
              {visibleProjects.map((p)=> (
                <ListItem key={p.project_id} button onClick={()=> setPickedProject(p)}>
                  <ListItemText primary={`${p.project_id} ${p.project_name}`} secondary={p.investment_address} />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
      </Dialog>

      {/* Formularz wysyłki zapytania dla wybranego projektu */}
      {pickedProject && (
        <MobileInquiryForm
          open={inquiryOpen}
          onClose={()=>{ setInquiryOpen(false); setProjectPickerOpen(null); setPickedProject(null); }}
          projectId={pickedProject.project_id}
          projectName={pickedProject.project_name}
        />
      )}

      {/* Wybór podwykonawcy pod POCR/POKR */}
      <Dialog open={subPickerOpen} onClose={()=>{ setSubPickerOpen(false); setPickedSub(null); setProjectPickerOpen(null); setPickedProject(null); }} fullWidth maxWidth="xs">
        <DialogTitle>Wybierz podwykonawcę</DialogTitle>
        <DialogContent dividers>
          {subsLoading ? (
            <Box sx={{ display:'flex', justifyContent:'center', py:2 }}><CircularProgress size={20} /></Box>
          ) : (
            <List>
              {subs.map((s)=> (
                <ListItem key={s.id} button onClick={()=>{ setPickedSub(s); if (projectPickerOpen==='pocr') { setPocrOpen(true); } else { setPokrOpen(true); } setSubPickerOpen(false); setActionsOpen(false); }}>
                  <ListItemText primary={s.name} secondary={`Umowa: ${s.contract_number || '—'}`} />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
      </Dialog>

      {/* Formularze POCR/POKR (otwierane po wyborze podwykonawcy) */}
      {pocrOpen && pickedProject && pickedSub && (
        <GeneratePocrDialog open={pocrOpen} onClose={()=>{ setPocrOpen(false); setPickedProject(null); setPickedSub(null); setProjectPickerOpen(null); }} subcontractor={pickedSub as any} project={pickedProject as any} onSubmit={()=> setPocrOpen(false)} />
      )}
      {pokrOpen && pickedProject && pickedSub && (
        <GeneratePokrDialog open={pokrOpen} onClose={()=>{ setPokrOpen(false); setPickedProject(null); setPickedSub(null); setProjectPickerOpen(null); }} subcontractor={pickedSub as any} project={pickedProject as any} onSubmit={()=> setPokrOpen(false)} />
      )}

      {/* FAB + dolny panel szybkich akcji (globalne, jak w Google Drive) */}
      {isAuthenticated && (
        <>
          <Fab color="primary" aria-label="dodaj" onClick={()=>setActionsOpen(true)} sx={{ position:'fixed', right: 16, bottom: `calc(16px + env(safe-area-inset-bottom))`, zIndex: 1200 }}>
            <AddIcon />
          </Fab>
          <Drawer
            anchor="bottom"
            open={actionsOpen}
            onClose={()=>setActionsOpen(false)}
            PaperProps={{ sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, pb: 'env(safe-area-inset-bottom)' } }}
          >
            <Box sx={{ pt: 1.5, px: 2 }}>
              <Box sx={{ width: 40, height: 4, bgcolor: 'grey.400', borderRadius: 999, mx: 'auto', mb: 2 }} />
              <Grid container spacing={2} sx={{ pb: 1 }}>
                <Grid item xs={4}>
                  <Button fullWidth onClick={handleScan} sx={{ display:'flex', flexDirection:'column', textTransform:'none' }}>
                    <Box sx={{ bgcolor:'primary.main', color:'#fff', width:56, height:56, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', mb: 0.5 }}>
                      <DocumentScannerOutlined />
                    </Box>
                    <Typography variant="caption">Skanuj dokumenty</Typography>
                  </Button>
                </Grid>
                <Grid item xs={4}>
                  <Button fullWidth onClick={handleReport} sx={{ display:'flex', flexDirection:'column', textTransform:'none' }}>
                    <Box sx={{ bgcolor:'primary.main', color:'#fff', width:56, height:56, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', mb: 0.5 }}>
                      <AssignmentOutlined />
                    </Box>
                    <Typography variant="caption">Raport budowy</Typography>
                  </Button>
                </Grid>
                <Grid item xs={4}>
                  <Button fullWidth onClick={handleInquiry} sx={{ display:'flex', flexDirection:'column', textTransform:'none' }}>
                    <Box sx={{ bgcolor:'primary.main', color:'#fff', width:56, height:56, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', mb: 0.5 }}>
                      <MailOutline />
                    </Box>
                    <Typography variant="caption">Wyślij zapytanie</Typography>
                  </Button>
                </Grid>
                <Grid item xs={4}>
                  <Button fullWidth onClick={openPocrPokr} sx={{ display:'flex', flexDirection:'column', textTransform:'none' }}>
                    <Box sx={{ bgcolor:'primary.main', color:'#fff', width:56, height:56, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', mb: 0.5 }}>
                      <DescriptionOutlined />
                    </Box>
                    <Typography variant="caption">POCR / POKR</Typography>
                  </Button>
                </Grid>
                <Grid item xs={4}>
                  <Button fullWidth onClick={handleWezwanie} sx={{ display:'flex', flexDirection:'column', textTransform:'none' }}>
                    <Box sx={{ bgcolor:'primary.main', color:'#fff', width:56, height:56, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', mb: 0.5 }}>
                      <WarningAmberOutlined />
                    </Box>
                    <Typography variant="caption">Wezwanie</Typography>
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Drawer>
          {/* Dialog: wybór sposobu skanowania (Aparat/Biblioteka) */}
          <Dialog open={scanChoiceOpen} onClose={()=>setScanChoiceOpen(false)} fullWidth maxWidth="xs" sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }} PaperProps={{ sx: { borderRadius: 3, mt: 'calc(88px + env(safe-area-inset-top))' } }}>
            <DialogTitle>Skanuj dokumenty</DialogTitle>
            <DialogContent dividers>
              <List>
                <ListItem button onClick={()=>{ try { fileInputRef.current?.click(); } catch {} }}>
                  <ListItemText primary="Aparat" secondary="Zrób zdjęcie" />
                </ListItem>
                <ListItem button onClick={()=>{ try { libraryInputRef.current?.click(); } catch {} }}>
                  <ListItemText primary="Biblioteka zdjęć" secondary="Wybierz zdjęcia" />
                </ListItem>
              </List>
            </DialogContent>
          </Dialog>
          {/* Sub-sheet: wybór POCR/POKR */}
          <Drawer
            anchor="bottom"
            open={pocrPokrOpen}
            onClose={()=>setPocrPokrOpen(false)}
            PaperProps={{ sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, pb: 'env(safe-area-inset-bottom)' } }}
          >
            <Box sx={{ pt: 1.5 }}>
              <Box sx={{ width: 40, height: 4, bgcolor: 'grey.400', borderRadius: 999, mx: 'auto', mb: 1 }} />
              <Box sx={{ display:'flex', alignItems:'center', px:1, pb: 1 }}>
                <IconButton onClick={()=>setPocrPokrOpen(false)} aria-label="wstecz"><ArrowBackIcon /></IconButton>
                <Typography variant="subtitle1">Wybierz dokument</Typography>
              </Box>
              <Divider />
              <List>
                <ListItem button onClick={handlePocr}><ListItemText primary="POCR" secondary="Przejdź do projektów" /></ListItem>
                <ListItem button onClick={handlePokr}><ListItemText primary="POKR" secondary="Przejdź do projektów" /></ListItem>
              </List>
            </Box>
          </Drawer>
          {/* Aparat */}
          <input ref={fileInputRef} type="file" accept="image/*" capture="environment" multiple onChange={onFilesSelected} style={{ display:'none' }} />
          {/* Biblioteka zdjęć */}
          <input ref={libraryInputRef} type="file" accept="image/*" multiple onChange={onFilesSelected} style={{ display:'none' }} />
        </>
      )}

      {/* Okejka potwierdzająca operacje (spójna z mobilnymi listami) */}
      <Dialog open={okOpen} onClose={()=>setOkOpen(false)} fullWidth maxWidth="xs" sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }} PaperProps={{ sx: { borderRadius: 3, mt: 'calc(88px + env(safe-area-inset-top))' } }}>
        <DialogTitle sx={{ textAlign:'center', fontWeight:700 }}>{okTitle}</DialogTitle>
        <DialogContent>
          <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', py: 4 }}>
            <CheckCircleOutlineIcon color="success" sx={{ fontSize: 56, mb: 1 }} />
            {okSubtitle && (<Typography variant="body2" color="text.secondary">{okSubtitle}</Typography>)}
          </Box>
        </DialogContent>
      </Dialog>

      <IncomingCallToast
        open={incomingOpen}
        fromDisplayName={incomingFrom || undefined}
        fromAvatarUrl={incomingAvatar || undefined}
        leftSeconds={incomingLeftSec}
        callKey={incomingCallKey}
        onAccept={() => {
          if (incomingMeet) window.open(incomingMeet, '_blank', 'noopener');
          if (incomingCallKey) broadcastCallResolved(incomingCallKey, 'accepted');
          setIncomingOpen(false);
          setIncomingFrom(null);
          setIncomingAvatar(null);
          setIncomingMeet(null);
          setIncomingCallKey(null);
        }}
        onDecline={() => {
          if (incomingCallKey) broadcastCallResolved(incomingCallKey, 'declined');
          setIncomingOpen(false);
          setIncomingFrom(null);
          setIncomingAvatar(null);
          setIncomingMeet(null);
          setIncomingCallKey(null);
        }}
      />
    </Box>
  );
};

export default MobileLayout;


